import React from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AIDistributionChart = ({ title, chartData, chartType = 'auto' }) => {
  // Validate input data
  const validChartData = Array.isArray(chartData) && chartData.length > 0 ? chartData : [];
  
  // Check if there's actually data to display
  const hasData = validChartData.length > 0 && validChartData.some(series => 
    Array.isArray(series.data) && series.data.length > 0 && series.data[0] > 0
  );
  
  // If no data, show empty state
  if (!hasData) {
    return (
      <div className="ai-chart-wrapper">
        <h4 className="ai-chart-title">{title}</h4>
        <div className="ai-chart-empty">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Generate default colors if not provided
  const defaultColors = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6c757d'
  ];

  // Determine chart type: auto-select pie for ≤5 categories, bar for >5
  let selectedChartType = chartType;
  if (chartType === 'auto') {
    selectedChartType = validChartData.length <= 5 ? 'pie' : 'bar';
  }

  // Extract labels and data
  const labels = validChartData.map(series => series.name);
  const dataValues = validChartData.map(series => series.data[0] || 0);
  const colors = validChartData.map((series, index) => 
    series.color || defaultColors[index % defaultColors.length]
  );

  const data = {
    labels: labels,
    datasets: [{
      label: 'Count',
      data: dataValues,
      backgroundColor: colors,
      borderColor: colors.map(color => color),
      borderWidth: selectedChartType === 'pie' ? 2 : 1,
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const total = data.datasets[0].data.reduce((acc, val) => acc + val, 0);
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const percentage = Math.round((value / total) * 100);
                return {
                  text: `${label}: ${value} (${percentage}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
            const percentage = Math.round((value / total) * 100);
            return `Count: ${value} (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11
          }
        },
        title: {
          display: true,
          text: 'Count',
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      }
    }
  };

  return (
    <div className="ai-chart-wrapper">
      <h4 className="ai-chart-title">{title}</h4>
      <div className="ai-chart-container">
        {selectedChartType === 'pie' ? (
          <Pie data={data} options={pieOptions} />
        ) : (
          <Bar data={data} options={barOptions} />
        )}
      </div>
    </div>
  );
};

export default AIDistributionChart;
